import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Card,
  Button,
  Row,
  Col,
  Spin,
  Space,
  Typography,
  Empty,
  Modal,
  Input,
  Checkbox,
  Tag,
  Tooltip,
} from 'antd';
import {
  MessageOutlined,
  SendOutlined,
  LeftOutlined,
  RightOutlined,
  StarFilled,
  StarOutlined,
  ExclamationCircleFilled,
  ExclamationCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';
import { useSelector, useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import { linkifyText } from '../../../utils/linkify';
import { isAdmin, isManager } from '../../../utils/userRoles';
import CommentAttachments from '../../../components/CommentAttachments/CommentAttachments';
import CommentAttachmentInput from '../../../components/CommentAttachments/CommentAttachmentInput';
import '../edit/OrderEdit.css';
import './OrderView.css';

const QUALITY_LABELS = [
  { key: 'qualityGhatTheek', label: 'घाट ठीक है' },
  { key: 'qualityStoneSahi', label: 'स्टोन सही है' },
  { key: 'qualityOilLaga', label: 'ऑयल लगा दिया' },
  { key: 'qualitySettingTheek', label: 'सेटिंग ठीक है' },
  { key: 'qualityNoScratch', label: 'कोई स्क्रैच नहीं है' },
  { key: 'qualityPolishTheek', label: 'पॉलिश ठीक है' },
  { key: 'qualityRhodiumTheek', label: 'रोडियम ठीक है' },
  { key: 'qualityEarringPush', label: 'इयररिंग का पुश सही काम कर रहा है' },
  { key: 'qualityLockBand', label: 'लॉक सही से बंद हो रहा है' },
];

function ImageGallery({ images, title }) {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);
  const list = Array.isArray(images) ? images : [];

  if (list.length === 0) return <span className="order-view-empty-text">—</span>;

  return (
    <div className="order-view-image-gallery">
      <div className="order-view-image-thumbs">
        {list.map((url, index) => (
          <button
            key={index}
            type="button"
            className="order-view-image-thumb"
            onClick={() => { setPreviewIndex(index); setPreviewOpen(true); }}
          >
            <img src={url} alt={`${title} ${index + 1}`} />
          </button>
        ))}
      </div>
      <Modal
        title={`${title} ${previewIndex + 1} of ${list.length}`}
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={null}
        width="auto"
        centered
        styles={{ body: { padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 } }}
      >
        {list.length > 0 && (
          <>
            {previewIndex > 0 && (
              <Button type="text" icon={<LeftOutlined />} onClick={() => setPreviewIndex((i) => i - 1)} />
            )}
            <img
              src={list[previewIndex]}
              alt={`${title} ${previewIndex + 1}`}
              style={{ maxHeight: '80vh', maxWidth: '70vw', objectFit: 'contain' }}
            />
            {previewIndex < list.length - 1 && (
              <Button type="text" icon={<RightOutlined />} onClick={() => setPreviewIndex((i) => i + 1)} />
            )}
          </>
        )}
      </Modal>
    </div>
  );
}

export default function OrderView() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [commentAttachments, setCommentAttachments] = useState([]);
  const [commentLoading, setCommentLoading] = useState(false);
  const [favoriteBusy, setFavoriteBusy] = useState(false);
  const [importantBusy, setImportantBusy] = useState(false);
  const chatMessagesRef = useRef(null);
  const { user: currentUser } = useSelector((state) => state.authReducer);
  const canToggleFavorite = isAdmin(currentUser) || isManager(currentUser);

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`/orders/${id}`)
      .then((res) => {
        if (!cancelled) setOrder(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = err.response?.data?.error || 'Failed to load order';
          dispatch(setMessage({ message: msg, type: 'error' }));
          navigate('/orders');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id, navigate, dispatch]);

  const handleToggleFavorite = async () => {
    if (!canToggleFavorite) return;
    if (!order?._id || favoriteBusy) return;
    const next = !order.isFavorite;
    setFavoriteBusy(true);
    setOrder((o) => (o ? { ...o, isFavorite: next } : o));
    try {
      const res = await axios.patch(`/orders/${order._id}/favorite`, { isFavorite: next });
      setOrder(res.data);
    } catch (err) {
      setOrder((o) => (o ? { ...o, isFavorite: !next } : o));
      const msg = err.response?.data?.error || 'Could not update favorite';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setFavoriteBusy(false);
    }
  };

  const handleAddComment = async () => {
    const trimmed = commentText?.trim();
    if (!trimmed) return;
    setCommentLoading(true);
    try {
      const res = await axios.post(`/orders/${id}/comments`, {
        text: trimmed,
        attachments: commentAttachments,
      });
      setOrder(res.data);
      setCommentText('');
      setCommentAttachments([]);
      dispatch(setMessage({ message: 'Comment added', type: 'success' }));
      setTimeout(() => { if (chatMessagesRef.current) chatMessagesRef.current.scrollTop = chatMessagesRef.current.scrollHeight; }, 100);
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to add comment';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setCommentLoading(false);
    }
  };

  const handleToggleImportant = async () => {
    if (!order?._id || importantBusy) return;
    const next = !order.isImportant;
    setImportantBusy(true);
    setOrder((o) => (o ? { ...o, isImportant: next } : o));
    try {
      const res = await axios.patch(`/orders/${order._id}/important`, { isImportant: next });
      setOrder(res.data);
    } catch (err) {
      setOrder((o) => (o ? { ...o, isImportant: !next } : o));
      const msg = err.response?.data?.error || 'Could not update important mark';
      dispatch(setMessage({ message: msg, type: 'error' }));
    } finally {
      setImportantBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="order-edit-page order-view-page">
        <div className="order-edit-loading">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!order) return null;

  const { Title } = Typography;
  const productImages = Array.isArray(order.productImages) ? order.productImages : [];
  const parcelImages = Array.isArray(order.parcelImages) ? order.parcelImages : [];
  const marking = Array.isArray(order.marking) ? order.marking : [];

  const statusColors = { open: 'blue', pending: 'orange', ready: 'green', dispatch: 'cyan', completed: 'green', cancelled: 'red', return: 'purple' };
  const orderReadyColors = { Yes: 'green', No: 'red', Incomplete: 'default' };

  return (
    <div className="order-edit-page order-view-page">
      <Space direction="vertical" size="large" style={{ width: '100%' }} className="order-view-space">
        <div className="order-view-header order-view-header-bar">
          <div className="order-view-header-title-wrap">
            <div className="order-view-title-row">
              <Title level={3} className="order-view-title">
                {order.orderNumber ? `Order #${order.orderNumber}` : 'View order'}
                {' '}
                <Tooltip title={order.isImportant ? 'Remove important mark' : 'Mark as important'}>
                  <button
                    type="button"
                    className={`order-view-important-btn${order.isImportant ? ' order-view-important-btn--on' : ''}`}
                    aria-label={order.isImportant ? 'Remove important mark' : 'Mark as important'}
                    aria-pressed={!!order.isImportant}
                    disabled={importantBusy}
                    onClick={handleToggleImportant}
                  >
                    {order.isImportant ? <ExclamationCircleFilled /> : <ExclamationCircleOutlined />}
                  </button>
                </Tooltip>
                <Tooltip title={canToggleFavorite ? (order.isFavorite ? 'Remove from favorites' : 'Mark as favorite') : 'Only admin/manager can change favorite'}>
                  <button
                    type="button"
                    className={`order-view-favorite-btn${order.isFavorite ? ' order-view-favorite-btn--on' : ''}`}
                    aria-label={order.isFavorite ? 'Remove from favorites' : 'Mark as favorite'}
                    aria-pressed={!!order.isFavorite}
                    disabled={!canToggleFavorite || favoriteBusy}
                    onClick={handleToggleFavorite}
                  >
                    {order.isFavorite ? <StarFilled /> : <StarOutlined />}
                  </button>
                </Tooltip>
              </Title>
              
            </div>
            <p className="order-view-subtitle">{order.customerName || '—'}</p>
            <div className="order-view-badges">
              <Tag color={statusColors[order.status] || 'default'}>
                {order.status === 'ready' ? 'Ready (send to QC)' : (order.status ? String(order.status).charAt(0).toUpperCase() + String(order.status).slice(1) : 'Open')}
              </Tag>
              <Tag color={orderReadyColors[order.orderReady] || 'default'}>{order.orderReady ?? 'Incomplete'}</Tag>
            </div>
          </div>
          <Button onClick={() => navigate(-1)} size="large" className="app-btn-cancel">
            <LeftOutlined /> Back
          </Button>
        </div>

        <Card className="order-view-card order-view-card-details">
          <div className="order-view-card-head">
            <Title level={5} className="order-view-section-title">Order details</Title>
          </div>
          <Row gutter={[24, 20]} className="order-view-details-grid">
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Customer name</span>
                <span className="order-view-value">{order.customerName ?? '—'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Order number</span>
                <span className="order-view-value">{order.orderNumber ?? '—'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Order date</span>
                <span className="order-view-value">{order.orderDate ? dayjs(order.orderDate).format('MMM D, YYYY') : '—'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Target date</span>
                <span className="order-view-value">{order.targetDate ? dayjs(order.targetDate).format('MMM D, YYYY') : '—'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Source of order</span>
                <span className="order-view-value">{order.sourceOfOrder ?? '—'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Mode of parcel</span>
                <span className="order-view-value">{order.modeOfParcel ?? '—'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Envelope ready</span>
                <span className="order-view-value">{order.envelopeReady === true ? 'Yes' : 'No'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Stone prepared</span>
                <span className="order-view-value">{order.stonePrepared === true ? 'Yes' : 'No'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Courier name</span>
                <span className="order-view-value">{order.courierName ?? '—'}</span>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <div className="order-view-field">
                <span className="order-view-label">Tracking number</span>
                <span className="order-view-value">{order.trackingNumber ?? '—'}</span>
              </div>
            </Col>
            <Col xs={24}>
              <div className="order-view-field order-view-field-images">
                <span className="order-view-label">Product images</span>
                <ImageGallery images={productImages} title="Product image" />
              </div>
            </Col>
          </Row>
        </Card>

        <Card className="order-view-card order-view-card-quality">
          <div className="order-view-card-head">
            <Title level={5} className="order-view-section-title">Quality check</Title>
          </div>
          <Row gutter={[24, 20]}>
            <Col xs={24} md={12}>
              <div className="order-view-quality-list">
                {QUALITY_LABELS.slice(0, 7).map(({ key, label }) => (
                  <div key={key} className="order-view-quality-item">
                    <Checkbox checked={order[key] === true} disabled />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
            </Col>
            <Col xs={24} md={12}>
              <div className="order-view-quality-list">
                {QUALITY_LABELS.slice(7, 9).map(({ key, label }) => (
                  <div key={key} className="order-view-quality-item">
                    <Checkbox checked={order[key] === true} disabled />
                    <span>{label}</span>
                  </div>
                ))}
              </div>
              <div className="order-view-field order-view-field-inline">
                <span className="order-view-label">रिंग साइज</span>
                <span className="order-view-value">{order.ringSize || '—'}</span>
              </div>
              <div className="order-view-field order-view-field-inline">
                <span className="order-view-label">समान की गिनती</span>
                <span className="order-view-value">{order.samaanGinti || '—'}</span>
              </div>
            </Col>
            <Col xs={24}>
              <div className="order-view-field">
                <span className="order-view-label">मार्किंग</span>
                <span className="order-view-value">{marking.length > 0 ? marking.join(', ') : '—'}</span>
              </div>
            </Col>
            <Col xs={24}>
              <div className="order-view-field order-view-field-images">
                <span className="order-view-label">पार्सल तस्वीर</span>
                <ImageGallery images={parcelImages} title="Parcel photo" />
              </div>
            </Col>
          </Row>
        </Card>

        <Card title={<span><MessageOutlined style={{ marginRight: 8 }} />Comments</span>} className="order-view-card order-edit-comments-card">
          <div className="order-edit-chat">
            <div className="order-edit-chat-messages" ref={chatMessagesRef}>
              {order.comments && order.comments.length > 0 ? (
                order.comments.map((item, idx) => {
                  const isSystem = !item.created_by;
                  const name = item.created_by ? (item.created_by.name || item.created_by.email || '—') : 'Kosha';
                  const initial = (name !== '—' && name !== 'Kosha' ? name.charAt(0) : isSystem ? '' : name.charAt(0)).toUpperCase();
                  const isOwn = currentUser?.email && item.created_by?.email === currentUser.email;
                  return (
                    <div
                      key={item._id || `${item.created_at}-${idx}`}
                      className={`order-edit-chat-bubble-wrap ${isOwn ? 'order-edit-chat-bubble-own' : ''}`}
                    >
                      {!isOwn && (
                        <div className={`order-edit-chat-avatar ${isSystem ? 'order-edit-chat-avatar-kosha' : ''}`}>
                          {isSystem ? (
                            <img src={`${process.env.PUBLIC_URL || ''}/KOSHA-logo.png`} alt="Kosha" />
                          ) : (
                            initial
                          )}
                        </div>
                      )}
                      <div className="order-edit-chat-bubble">
                        <div className="order-edit-chat-bubble-header">
                          <span className="order-edit-chat-name">{isOwn ? 'You' : name}</span>
                          <span className="order-edit-chat-time">
                            {dayjs(item.created_at).isSame(dayjs(), 'day')
                              ? `Today ${dayjs(item.created_at).format('h:mm A')}`
                              : dayjs(item.created_at).format('MMM D, YYYY h:mm A')}
                          </span>
                        </div>
                        <div className="order-edit-chat-bubble-text" style={{ whiteSpace: 'pre-line' }}>{linkifyText(item.text)}</div>
                        {item.attachments && item.attachments.length > 0 && (
                          <CommentAttachments attachments={item.attachments} className="order-edit-chat-bubble-attachments" />
                        )}
                      </div>
                      {isOwn && <div className="order-edit-chat-avatar order-edit-chat-avatar-own">{initial}</div>}
                    </div>
                  );
                })
              ) : (
                <Empty description="No comments yet" image={Empty.PRESENTED_IMAGE_SIMPLE} className="order-edit-chat-empty" />
              )}
            </div>
            <div className="order-edit-comments-add">
              <Input.TextArea
                rows={2}
                placeholder="Type a message..."
                value={commentText}
                onChange={(e) => setCommentText(e.target.value)}
                onPressEnter={(e) => { if (!e.shiftKey) { e.preventDefault(); handleAddComment(); } }}
                className="order-edit-comment-input"
                autoSize={{ minRows: 2, maxRows: 4 }}
                maxLength={500}
              />
              <div className="order-edit-comment-footer">
                <CommentAttachmentInput
                  value={commentAttachments}
                  onChange={setCommentAttachments}
                  disabled={commentLoading}
                />
                <span className="order-edit-comment-count">{commentText.length}/500 characters</span>
                <Button
                  type="text"
                  shape="circle"
                  icon={<SendOutlined className="order-edit-comment-icon" />}
                  onClick={handleAddComment}
                  loading={commentLoading}
                  disabled={!commentText?.trim()}
                  className="order-edit-comment-add-btn"
                  aria-label="Add comment"
                />
              </div>
            </div>
          </div>
        </Card>
      </Space>
    </div>
  );
}
